import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Eye } from "@phosphor-icons/react";
import { CloseIcon } from "../../../assets/icons";
import InboxDocumentViewer from "../../Inbox/components/InboxDocumentViewer";
import {
  CloseButton,
  ConfirmationValue,
  DetailPane,
  DocumentPane,
  DocumentPaneBody,
  DocumentPaneToolbar,
  EvidenceGrid,
  EvidenceLabel,
  EvidenceValue,
  EventCard,
  EventHeader,
  EventList,
  HashValue,
  HideDocumentButton,
  IntegrityBadge,
  Modal,
  ModalBody,
  Overlay,
  Section,
  SectionTitle,
  Title,
  ViewDocumentLink,
} from "./VaSubmissionEvidenceModalStyled";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      })
    : "Not recorded";

const formatPayload = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

const eventLabel = (eventType) =>
  (eventType || "event")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const VaSubmissionEvidenceModal = ({
  open,
  onClose,
  loading,
  error,
  evidenceData,
}) => {
  const [documentPaneOpen, setDocumentPaneOpen] = useState(false);
  const upload = evidenceData?.upload || {};
  const events = evidenceData?.evidence || [];
  const integrity = evidenceData?.integrity || {};

  // The pane is scoped to one submission's evidence view — closing the
  // modal, or reopening it for a different upload, must not carry a stale
  // document viewer over into the next open.
  useEffect(() => {
    if (!open) {
      setDocumentPaneOpen(false);
    }
  }, [open]);
  useEffect(() => {
    setDocumentPaneOpen(false);
  }, [upload.id]);

  if (!open) return null;

  const canViewDocument = Boolean(upload.document_id);

  return (
    <Overlay onClick={onClose}>
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="va-evidence-title"
        $withDocument={documentPaneOpen}
        onClick={(event) => event.stopPropagation()}
      >
        <CloseButton onClick={onClose} aria-label="Close VA submission evidence">
          <CloseIcon />
        </CloseButton>
        <Title id="va-evidence-title">VA submission evidence</Title>

        {loading && <EvidenceValue>Loading the complete evidence ledger…</EvidenceValue>}
        {error && <EvidenceValue>{error}</EvidenceValue>}

        {!loading && !error && (
          <ModalBody>
            <DetailPane>
              <Section>
                <SectionTitle>Confirmation</SectionTitle>
                <EvidenceGrid>
                  <div>
                    <EvidenceLabel>VA GUID / confirmation identifier</EvidenceLabel>
                    <ConfirmationValue>{upload.guid || "Not returned"}</ConfirmationValue>
                  </div>
                  <div>
                    <EvidenceLabel>Current VA status</EvidenceLabel>
                    <EvidenceValue>
                      {upload.status_display || upload.status || "Not recorded"}
                    </EvidenceValue>
                  </div>
                  <div>
                    <EvidenceLabel>VA-reported receipt time</EvidenceLabel>
                    <EvidenceValue>{formatDateTime(upload.va_received_at)}</EvidenceValue>
                  </div>
                  <div>
                    <EvidenceLabel>When DMS observed receipt</EvidenceLabel>
                    <EvidenceValue>
                      {formatDateTime(upload.va_received_observed_at)}
                    </EvidenceValue>
                  </div>
                </EvidenceGrid>
              </Section>

              <Section>
                <SectionTitle>Exact submitted document</SectionTitle>
                <EvidenceGrid>
                  <div>
                    <EvidenceLabel>File</EvidenceLabel>
                    <EvidenceValue>{upload.document_name || "Not recorded"}</EvidenceValue>
                    {canViewDocument && !documentPaneOpen && (
                      <ViewDocumentLink
                        type="button"
                        onClick={() => setDocumentPaneOpen(true)}
                      >
                        <Eye size={14} weight="bold" aria-hidden="true" />
                        View document
                      </ViewDocumentLink>
                    )}
                  </div>
                  <div>
                    <EvidenceLabel>Size</EvidenceLabel>
                    <EvidenceValue>
                      {upload.document_size_bytes
                        ? `${upload.document_size_bytes.toLocaleString()} bytes`
                        : "Not recorded"}
                    </EvidenceValue>
                  </div>
                  <div>
                    <EvidenceLabel>PDF SHA-256</EvidenceLabel>
                    <HashValue>{upload.document_sha256 || "Not recorded"}</HashValue>
                  </div>
                  <div>
                    <EvidenceLabel>Metadata SHA-256</EvidenceLabel>
                    <HashValue>{upload.metadata_sha256 || "Not recorded"}</HashValue>
                  </div>
                </EvidenceGrid>
              </Section>

              <Section>
                <SectionTitle>Transport and integrity</SectionTitle>
                <EvidenceGrid>
                  <div>
                    <EvidenceLabel>VA upload HTTP status</EvidenceLabel>
                    <EvidenceValue>{upload.upload_http_status || "Not recorded"}</EvidenceValue>
                  </div>
                  <div>
                    <EvidenceLabel>VA upload ETag</EvidenceLabel>
                    <HashValue>{upload.upload_etag || "Not returned"}</HashValue>
                  </div>
                  <div>
                    <EvidenceLabel>Evidence chain</EvidenceLabel>
                    <IntegrityBadge valid={integrity.chain_valid}>
                      {integrity.chain_valid ? "Verified" : "Verification failed"}
                    </IntegrityBadge>
                  </div>
                  <div>
                    <EvidenceLabel>Recorded events</EvidenceLabel>
                    <EvidenceValue>{integrity.event_count ?? events.length}</EvidenceValue>
                  </div>
                </EvidenceGrid>
              </Section>

              <Section>
                <SectionTitle>Status and response timeline</SectionTitle>
                <EventList>
                  {events.length === 0 && (
                    <EvidenceValue>No evidence events recorded.</EvidenceValue>
                  )}
                  {events.map((event) => (
                    <EventCard key={event.id || event.sequence}>
                      <EventHeader>
                        <strong>
                          #{event.sequence} {eventLabel(event.event_type)}
                        </strong>
                        <span>{formatDateTime(event.observed_at)}</span>
                      </EventHeader>
                      <EvidenceValue>
                        {event.previous_status && event.status
                          ? `${event.previous_status} → ${event.status}`
                          : event.status || "Status not recorded"}
                        {event.http_status ? ` · HTTP ${event.http_status}` : ""}
                        {event.etag ? ` · ETag ${event.etag}` : ""}
                      </EvidenceValue>
                      {event.detail && <EvidenceValue>{event.detail}</EvidenceValue>}
                      {(event.response_payload &&
                        Object.keys(event.response_payload).length > 0) && (
                        <details>
                          <summary>VA response data</summary>
                          <pre>{formatPayload(event.response_payload)}</pre>
                        </details>
                      )}
                      <HashValue>Event hash: {event.event_hash}</HashValue>
                    </EventCard>
                  ))}
                </EventList>
              </Section>
            </DetailPane>

            {documentPaneOpen && canViewDocument && (
              <DocumentPane>
                <DocumentPaneToolbar>
                  <HideDocumentButton
                    type="button"
                    onClick={() => setDocumentPaneOpen(false)}
                  >
                    Hide document
                  </HideDocumentButton>
                </DocumentPaneToolbar>
                <DocumentPaneBody>
                  <InboxDocumentViewer
                    documentId={upload.document_id}
                    documentName={upload.document_name}
                  />
                </DocumentPaneBody>
              </DocumentPane>
            )}
          </ModalBody>
        )}
      </Modal>
    </Overlay>
  );
};

VaSubmissionEvidenceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  evidenceData: PropTypes.shape({
    upload: PropTypes.object,
    evidence: PropTypes.arrayOf(PropTypes.object),
    integrity: PropTypes.object,
  }),
};

VaSubmissionEvidenceModal.defaultProps = {
  loading: false,
  error: "",
  evidenceData: null,
};

export default VaSubmissionEvidenceModal;
